/**
 * Clients who are still on the original agreement and need the addendum.
 *
 * New clients sign the current agreement at intake. Existing clients only move off LEGACY when
 * someone puts the addendum in front of them. This list is that chase.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AgreementApi, {
  AGREEMENT_LABELS,
  PendingAgreementRow,
  PendingAgreementsDto,
} from '../../api/AgreementApi';

function dateLabel(value: string | null): string {
  if (!value) return '—';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

const ManagerPendingAgreementsView: React.FC = () => {
  const navigate = useNavigate();
  const [dto, setDto] = useState<PendingAgreementsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDto(await AgreementApi.listPending());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending agreements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: PendingAgreementRow[] = dto?.needAddendum ?? [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Agreements
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Clients still on the original agreement. Open a row to record the addendum on the client page.
      </Typography>

      {dto && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {dto.needAddendum.length} need the addendum. {dto.onNewTerms} of {dto.total} are already on
          the newer terms. {dto.awaitingSignature.length} have a version marked but no signature date.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Agreement</TableCell>
                <TableCell>Signature date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No clients need the addendum.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.User_guid}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/user?User_guid=${row.User_guid}`)}
                  >
                    <TableCell>{row.name || '(name not recorded)'}</TableCell>
                    <TableCell>
                      {AGREEMENT_LABELS[row.version] ?? row.version}
                    </TableCell>
                    <TableCell>{dateLabel(row.signedAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status.reason} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default ManagerPendingAgreementsView;
